import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { readOnly } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import BroadcastsListController from 'cio/pods/broadcasts/list-controller';
import { ALL_CHANNELS, ALL_NEWSLETTERS } from 'cio/utils/campaign-newsletter-filters';

import AlertsService from 'cio/services/alerts';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import ExportsService from 'cio/services/exports';
import { ChannelType } from 'cio/models/newsletter';
import TagModel from 'cio/models/tag';

const SORT_DEFAULTS: Record<StatusFilterType, string> = {
  all: 'updated',
  drafted: 'updated',
  scheduled: 'updated',
  sent: 'sent',
};

type StatusFilterType = 'all' | 'drafted' | 'scheduled' | 'sent';

export interface TotalCounts {
  sent: number;
  drafted: number;
  scheduled: number;
}

export default class IndexController extends BroadcastsListController {
  @service alerts!: AlertsService;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service exports!: ExportsService;

  queryParams = [
    'channel',
    'type',
    'sendToUnsubscribed',
    'startTime',
    'endTime',
    'search',
    'tagIds',
    'page',
    'sort',
    'asc',
    'subscriptionTopicId',
    'objectTypeIds',
  ];
  channel: 'all' | ChannelType = ALL_CHANNELS;
  type: StatusFilterType = ALL_NEWSLETTERS;
  startTime?: string;
  endTime?: string;

  @readOnly('broadcasts.meta.totals') totalCounts!: TotalCounts;
  @readOnly('broadcasts.meta.pagination.total') total!: number;

  setDefaultQueryParams() {
    super.setDefaultQueryParams();
    this.setProperties({
      channel: ALL_CHANNELS,
      type: ALL_NEWSLETTERS,
    });
  }

  @computed('search', 'type', 'channel', 'startTime', 'endTime')
  get hasAnyFilters() {
    return (
      !isEmpty(this.search) ||
      this.type !== ALL_NEWSLETTERS ||
      this.channel !== ALL_CHANNELS ||
      !isEmpty(this.startTime) ||
      !isEmpty(this.endTime)
    );
  }

  @action
  didChangeType(type: StatusFilterType) {
    this.setProperties({
      page: 1,
      type,
      sort: SORT_DEFAULTS[type], // Change default sorting based on the selected type
    });
  }

  @action
  didChangeTimePeriod({ startTime, endTime }: { startTime: string; endTime: string }) {
    this.setProperties({
      startTime,
      endTime,
    });
  }

  @action
  afterNewsletterAction() {
    return this.send('reloadModel'); // reload route model to update pagination counts
  }

  @action
  onTagClicked(tag: TagModel) {
    this.set('tagIds', [tag.id]);
  }

  @action
  didChangeTopic(topic: { label: string; value: string } | null) {
    this.subscriptionTopicId = topic?.value ?? null;
  }
}
